<script setup>
// Dialog inline acionado pelo header da conversa para criar lead diretamente.
// Reusa SettingsToggleSection/SettingsFieldSection? Não - esses são pra
// formulários de Settings. Aqui usamos Input + Dialog padrão do design system.
//
// Quando o usuário envia, dispara `accounts/v2/leads` direto e devolve o lead
// criado pro pai (que atualiza o badge no header).
import { ref, computed, watch } from 'vue';
import LeadsAPI from 'dashboard/api/leads';
import LeadIntentsAPI from 'dashboard/api/leadIntents';
import PipelinesAPI from 'dashboard/api/pipelines';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';

const props = defineProps({
  conversationId: { type: [Number, String], default: null },
  contactId: { type: [Number, String], default: null },
});

const emit = defineEmits(['created']);

const dialogRef = ref(null);
const isSubmitting = ref(false);
const intents = ref([]);
const pipelines = ref([]);

const form = ref({
  lead_intent_id: null,
  classification: 'warm',
  pipeline_id: null,   // null = usar mapping/default do bot
  value_cents: '',
  summary: '',
});

const canSubmit = computed(() =>
  Boolean(form.value.lead_intent_id) && Boolean(props.contactId)
);

// Opções no formato exigido pelo SelectInput ({ value, label }).
const intentOptions = computed(() => [
  { value: '', label: '- selecione -' },
  ...intents.value.map(i => ({ value: i.id, label: i.name })),
]);
const pipelineOptions = computed(() => [
  { value: '', label: 'Padrão da intenção' },
  ...pipelines.value.map(p => ({ value: p.id, label: p.name })),
]);

async function loadOptions() {
  try {
    const [iRes, pRes] = await Promise.all([
      LeadIntentsAPI.get(),
      PipelinesAPI.get(),
    ]);
    intents.value = iRes?.data?.lead_intents || iRes?.data || [];
    pipelines.value = pRes?.data?.pipelines || pRes?.data || [];
  } catch {
    intents.value = [];
    pipelines.value = [];
  }
}

function resetForm() {
  form.value = {
    lead_intent_id: null,
    classification: 'warm',
    pipeline_id: null,
    value_cents: '',
    summary: '',
  };
}

defineExpose({
  open() {
    resetForm();
    loadOptions();
    dialogRef.value?.open();
  },
  close() {
    dialogRef.value?.close();
  },
});

async function submit() {
  if (!canSubmit.value || isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    const payload = {
      contact_id: Number(props.contactId),
      conversation_id: Number(props.conversationId),
      lead_intent_id: Number(form.value.lead_intent_id),
      classification: form.value.classification,
      summary: form.value.summary || null,
    };
    if (form.value.pipeline_id) payload.pipeline_id = Number(form.value.pipeline_id);
    if (form.value.value_cents) {
      // Usuário digita em reais (R$ 1.234,56); convertemos pra centavos
      const cents = Math.round(Number(String(form.value.value_cents).replace(',', '.')) * 100);
      if (!Number.isNaN(cents) && cents > 0) payload.value_cents = cents;
    }

    const { data: lead } = await LeadsAPI.create({ lead: payload });
    emit('created', lead);
    useAlert('Lead criado e vinculado a esta conversa.');
    dialogRef.value?.close();
  } catch (e) {
    useAlert(
      e?.response?.data?.error
        || e?.response?.data?.errors?.join?.(', ')
        || 'Falha ao criar lead.'
    );
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div class="flex flex-col gap-4">
      <div>
        <h3 class="text-heading-3 text-n-slate-12 mb-1">Criar lead a partir da conversa</h3>
        <p class="text-body-small text-n-slate-11 mb-0">
          Vincula um lead novo a este contato e a esta conversa. A IA continua atuando normalmente.
        </p>
      </div>

      <div>
        <label class="text-body-small text-n-slate-12 font-medium mb-1.5">
          Intenção
        </label>
        <SelectInput v-model="form.lead_intent_id" :options="intentOptions" />
        <p class="text-label-small text-n-slate-10 mt-1 mb-0">
          A intenção define o pipeline destino padrão e o modelo de follow-up sugerido pela IA.
        </p>
      </div>

      <div>
        <label class="text-body-small text-n-slate-12 font-medium mb-1.5">Classificação</label>
        <div class="flex gap-2">
          <button
            v-for="c in [
              { value: 'hot',  label: 'Quente', cls: 'border-n-ruby-9 bg-n-ruby-3 text-n-ruby-12' },
              { value: 'warm', label: 'Morno',  cls: 'border-n-amber-9 bg-n-amber-3 text-n-amber-12' },
              { value: 'cold', label: 'Frio',   cls: 'border-n-slate-7 bg-n-alpha-2 text-n-slate-11' },
            ]"
            :key="c.value"
            type="button"
            class="flex-1 px-3 py-1.5 rounded-md border text-body-small font-medium transition-colors"
            :class="form.classification === c.value ? c.cls : 'border-n-weak text-n-slate-11 hover:border-n-strong'"
            @click="form.classification = c.value"
          >
            {{ c.label }}
          </button>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-body-small text-n-slate-12 font-medium mb-1.5">
            Pipeline (opcional)
          </label>
          <SelectInput v-model="form.pipeline_id" :options="pipelineOptions" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-body-small text-n-slate-12 font-medium mb-1.5">
            Valor estimado (R$)
          </label>
          <Input
            v-model="form.value_cents"
            placeholder="Ex: 1500,00"
          />
        </div>
      </div>

      <div>
        <label class="text-body-small text-n-slate-12 font-medium mb-1.5">Resumo</label>
        <textarea
          v-model="form.summary"
          rows="3"
          placeholder="O que o cliente quer? Contexto rápido para o próximo agente ou para a IA."
          class="w-full rounded-md border border-n-weak bg-n-solid-1 px-3 py-2 text-body-main text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand resize-none"
        />
      </div>

      <div class="flex justify-end gap-2 pt-2">
        <NextButton faded slate label="Cancelar" @click="dialogRef?.close()" />
        <NextButton
          :is-loading="isSubmitting"
          :disabled="!canSubmit"
          label="Criar lead"
          icon="i-lucide-target"
          @click="submit"
        />
      </div>
    </div>
  </Dialog>
</template>
